<template>
  <section class="requests-page">
    <div v-if="isLoading" class="loading-overlay" role="status" aria-live="polite">
      <div class="loading-box">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p>Consultando seus agendamentos...</p>
      </div>
    </div>

    <div class="requests-shell">
      <header class="requests-header">
        <div>
          <p class="eyebrow">Meus agendamentos</p>
          <h1>Consulta de solicitações</h1>
          <p class="header-copy">
            Acompanhe o status das suas reservas e solicite cancelamento quando necessário.
          </p>
        </div>

        <div class="user-card">
          <span>E-mail consultado</span>
          <strong>{{ emails }}</strong>
        </div>
      </header>

      <section class="filters-panel" aria-label="Filtros de agendamentos">
        <label class="filter-field" for="selecionadia">
          <span>Data</span>
          <input id="selecionadia" type="date" v-model="filtrodia" @change="buscar" />
        </label>

        <label class="filter-field" for="filtroIdade">
          <span>Faixa etária/Série</span>
          <select id="filtroIdade" v-model="filtroIdade" @change="buscar">
            <option value="">Todas</option>
            <option v-for="opcao in opcoesIdade" :key="opcao" :value="opcao">
              {{ opcao }}
            </option>
          </select>
        </label>

        <div class="filter-actions">
          <button type="button" class="btn btn-primary" @click="buscar">
            Buscar
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="limparFiltros">
            Limpar
          </button>
        </div>
      </section>

      <div class="results-toolbar">
        <div>
          <strong>{{ registrosFiltrados.length }}</strong>
          <span>{{ registrosFiltrados.length === 1 ? 'agendamento encontrado' : 'agendamentos encontrados' }}</span>
        </div>
        <p>Os registros são exibidos do mais recente para o mais antigo.</p>
      </div>

      <section v-if="registrosFiltrados.length" class="records-list">
        <article
          v-for="(registro, index) in registrosFiltrados"
          :key="registroTicket(registro) || registro.id || index"
          class="record-card"
          :class="{ agency: isAgencia(registro) }"
        >
          <div class="record-main">
            <div class="record-title-line">
              <h2>{{ valorCampo(registro.instituicao) }}</h2>
              <span class="status-badge" :class="statusClass(statusRegistro(registro))">
                {{ statusLabel(statusRegistro(registro)) }}
              </span>
            </div>

            <div class="record-meta">
              <span>Ticket {{ valorCampo(registroTicket(registro)) }}</span>
              <span>Entrada {{ formatarData(registro.logsregistro) }}</span>
            </div>

            <div class="route-highlight">
              <span>Roteiro, horário e dia</span>
              <strong>{{ extrairoteiro(registro.idbruto || registro.id) }}</strong>
            </div>

            <dl class="info-grid">
              <div>
                <dt>CNPJ</dt>
                <dd>{{ valorCampo(registro.cnpj) }}</dd>
              </div>
              <div>
                <dt>Visitantes</dt>
                <dd>{{ valorCampo(registro.quantidade) }}</dd>
              </div>
              <div>
                <dt>Faixa etária/Série</dt>
                <dd>{{ valorCampo(registro.series) }}</dd>
              </div>
              <div>
                <dt>Contato</dt>
                <dd>{{ valorCampo(registro.nomecontato) }}</dd>
              </div>
            </dl>

            <div v-if="isAgencia(registro)" class="agency-block">
              <p>Agência responsável</p>
              <dl>
                <div>
                  <dt>Nome</dt>
                  <dd>{{ valorCampo(registro.instituicao2) }}</dd>
                </div>
                <div>
                  <dt>CNPJ</dt>
                  <dd>{{ valorCampo(registro.cnpj2) }}</dd>
                </div>
                <div>
                  <dt>E-mail</dt>
                  <dd>{{ valorCampo(registro.obs3) }}</dd>
                </div>
                <div>
                  <dt>Telefone</dt>
                  <dd>{{ valorCampo(registro.obs2) }}</dd>
                </div>
              </dl>
            </div>
          </div>

          <aside class="record-side">
            <button
              v-if="statusRegistro(registro) !== '0'"
              type="button"
              class="btn btn-danger"
              @click="abrirModalCancelamento(registro)"
            >
              Solicitar cancelamento
            </button>

            <div class="ticket-note">
              <span>Anote seu ticket</span>
              <strong>{{ valorCampo(registroTicket(registro)) }}</strong>
            </div>
          </aside>

          <div class="accordion details-accordion" :id="`accordionRequest${index}`">
            <div class="accordion-item">
              <h3 class="accordion-header" :id="`headingRequest${index}`">
                <button
                  class="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="`#collapseRequest${index}`"
                  aria-expanded="false"
                  :aria-controls="`collapseRequest${index}`"
                >
                  Detalhes
                </button>
              </h3>
              <div
                :id="`collapseRequest${index}`"
                class="accordion-collapse collapse"
                :aria-labelledby="`headingRequest${index}`"
                :data-bs-parent="`#accordionRequest${index}`"
              >
                <div class="accordion-body">
                  <dl class="details-grid">
                    <div>
                      <dt>E-mail do responsável</dt>
                      <dd>{{ valorCampo(registro.email) }}</dd>
                    </div>
                    <div>
                      <dt>Endereço</dt>
                      <dd>{{ valorCampo(registro.endereco) }}</dd>
                    </div>
                    <div>
                      <dt>Tipo da instituição</dt>
                      <dd>{{ valorCampo(registro.tipo) }}</dd>
                    </div>
                    <div>
                      <dt>Deficiência/necessidades</dt>
                      <dd>{{ formatarDeficiencia(registro.deficiencia) }}</dd>
                    </div>
                    <div>
                      <dt>Cargo</dt>
                      <dd>{{ valorCampo(registro.cargo) }}</dd>
                    </div>
                    <div>
                      <dt>WhatsApp</dt>
                      <dd>{{ valorCampo(registro.obswp) }}</dd>
                    </div>
                    <div class="details-wide">
                      <dt>Informações de contato</dt>
                      <dd>{{ valorCampo(registro.obs) }}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section v-else class="empty-state">
        <h2>Nenhum agendamento encontrado</h2>
        <p>Revise os filtros ou selecione outra data.</p>
      </section>
    </div>

    <div class="modal fade" id="modalCancelamento" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content" id="cancelare">
          <div class="modal-header">
            <h5 class="modal-title">Solicitar cancelamento</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
          </div>

          <div class="modal-body">
            <label class="form-label" for="justificativaCancelamento">Justificativa obrigatória</label>
            <textarea
              id="justificativaCancelamento"
              class="form-control"
              rows="4"
              v-model="justificativaCancelamento"
              placeholder="Explique o motivo do cancelamento..."
            ></textarea>
            <small class="text-muted d-block mt-2">
              Ticket: {{ valorCampo(registroTicket(registroSelecionado || {})) }} · E-mail:
              {{ valorCampo(registroSelecionado?.email) }}
            </small>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Voltar</button>
            <button type="button" class="btn btn-danger" @click="confirmarCancelamento">
              Enviar solicitação
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref, onMounted } from "vue";
import axios from "axios";
import { isMasterFull } from "@/utils/auth";

export default {
  setup() {
    const registroSelecionado = ref(null);
    const justificativaCancelamento = ref("");
    const filtrodia = ref("");
    const filtroIdade = ref("");
    const administrador = ref(false);
    const eadminsera = isMasterFull();
    const registros = ref([]);
    const isLoading = ref(false);
    const emails = localStorage.getItem("userEmail");

    const opcoesIdade = [
      "1º ano fundamental",
      "2º ano fundamental",
      "3º ano fundamental",
      "4º ano fundamental",
      "5º ano fundamental",
      "6º ano fundamental",
      "7º ano fundamental",
      "8º ano fundamental",
      "9º ano fundamental",
      "1º Ano Médio",
      "2º Ano Médio",
      "3º Ano Médio",
      "Criança",
      "Jovem",
      "Adulto",
      "Idosos",
      "Superior",
      "Educação Infantil",
      "Professor",
    ];

    const valorCampo = (valor) => {
      if (Array.isArray(valor)) {
        return valor.length
          ? valor.map((item) => valorCampo(item)).join(", ")
          : "Informações não preenchidas";
      }

      if (valor && typeof valor === "object") {
        return Object.values(valor).filter(Boolean).join(" - ") || "Informações não preenchidas";
      }

      return valor ? valor : "Informações não preenchidas";
    };

    const registroTicket = (registro) => registro?.bid || registro?.ticket || "";

    const statusRegistro = (registro) => registro?.estadoaprovacao ?? registro?.aprovacao ?? "pendente";

    const statusLabel = (status) => {
      const labels = {
        0: "Cancelado",
        1: "Confirmado",
        2: "Reservado",
        4: "Cancelamento solicitado",
        pendente: "Pendente",
      };

      return labels[status] || "Pendente";
    };

    const statusClass = (status) => {
      const classes = {
        0: "status-cancelado",
        1: "status-confirmado",
        2: "status-reservado",
        4: "status-cancelamento",
        pendente: "status-pendente",
      };

      return classes[status] || "status-pendente";
    };

    const formatarDeficiencia = (deficiencia) => {
      if (!deficiencia || (Array.isArray(deficiencia) && !deficiencia.length)) {
        return "Informações não preenchidas";
      }

      if (!Array.isArray(deficiencia)) {
        return valorCampo(deficiencia);
      }

      return deficiencia
        .map((item) => {
          if (!item || typeof item !== "object") {
            return valorCampo(item);
          }

          const partes = [];

          if (item.tipo) {
            partes.push(item.tipo);
          }

          if (item.quantidade) {
            partes.push(`${item.quantidade} ${Number(item.quantidade) === 1 ? "pessoa" : "pessoas"}`);
          }

          if (item.descricao) {
            partes.push(item.descricao);
          }

          return partes.join(" - ") || "Informações não preenchidas";
        })
        .join("; ");
    };

    const isAgencia = (registro) =>
      registro?.agencia === "É uma agência" ||
      registro?.agencia === "Ã‰ uma agÃªncia" ||
      Boolean(registro?.cnpj2);

    const extrairoteiro = (ide) => {
      const texto = String(ide ?? "");
      const regex = /(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/;
      const match = texto.match(regex);

      if (!match) {
        return texto || "Informações não preenchidas";
      }

      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const day = parseInt(match[3], 10);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();

      const daysOfWeek = [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado",
      ];
      const dayName = daysOfWeek[dayOfWeek];

      const isJuly2024 = year === 2024 && month === 7;
      const isWeekend = dayOfWeek === 6 || dayOfWeek === 0;

      if (match[7] === "14" || isJuly2024 || isWeekend || texto.endsWith("f")) {
        return texto.replace(regex, `Roteiro Livre - posição: $7 | $4h$5 | $3/$2/$1 (${dayName})`);
      }

      return texto.replace(regex, `Roteiro $7 | $4h$5 | $3/$2/$1 (${dayName})`);
    };

    const formatarData = (dataISO) => {
      if (!dataISO) return "Informações não preenchidas";

      const data = new Date(dataISO);
      if (Number.isNaN(data.getTime())) return dataISO;

      const opcoesData = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      };
      const opcoesHora = {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      };

      return `${data.toLocaleDateString("pt-BR", opcoesData)} ${data.toLocaleTimeString("pt-BR", opcoesHora)}`;
    };

    const buscar = () => {
      carregarRegistros(emails, filtrodia.value, filtroIdade.value);
    };

    const abrirModalCancelamento = (registro) => {
      registroSelecionado.value = registro;
      justificativaCancelamento.value = "";

      const modalEl = document.getElementById("modalCancelamento");
      const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
      modal.show();
    };

    const confirmarCancelamento = () => {
      const just = justificativaCancelamento.value?.trim();
      if (!just) {
        alert("A justificativa é obrigatória.");
        return;
      }

      const registro = registroSelecionado.value;
      if (!registro) {
        alert("Registro não encontrado.");
        return;
      }

      aprovar(registroTicket(registro), 4, registro.email, just);

      const modalEl = document.getElementById("modalCancelamento");
      const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
      modal.hide();
    };

    const aprovar = (identifica, parametro, emaild, justificativa = "") => {
      axios
        .post("https://museucatavento.org.br/sistema-agendamento/api4.php", {
          identificador: identifica,
          aprovacao: parametro,
          email: emaild,
          justificativa,
        })
        .then((response) => {
          if (response.data == "1") {
            const registroAtualizado = registros.value.find((r) => registroTicket(r) === identifica);
            if (registroAtualizado) {
              registroAtualizado.estadoaprovacao = parametro.toString();
              registroAtualizado.aprovacao = parametro.toString();
            }

            alert("Status atualizado. Aguarde alguns segundos para visualizar a confirmação.");

            if (emaild !== "admin.agendamento@cataventomaster.com") {
              enviaraprovacao(emaild, identifica, parametro);
            }
          } else {
            alert("Houve algum problema com sua solicitação.");
          }
        })
        .catch((error) => {
          console.error(error);
          alert("Houve algum erro, por favor, tente novamente em instantes.");
        });
    };

    const enviaraprovacao = (emaild, identifica, parametro) => {
      const registro = registros.value.find((r) => registroTicket(r) === identifica);

      if (!registro) {
        alert("Registro não encontrado.");
        return;
      }

      const dadosRegistro = {
        seuemail: emaild,
        bid: identifica,
        parametro,
        instituicao: registro.instituicao || "Informações não preenchidas",
        cnpj: registro.cnpj || "Informações não preenchidas",
        diamaster: registro.diamaster || "Informações não preenchidas",
        estadoaprovacao: statusRegistro(registro),
        email: registro.email || "Informações não preenchidas",
        endereco: registro.endereco || "Informações não preenchidas",
        quantidade: registro.quantidade || "Informações não preenchidas",
        series: valorCampo(registro.series),
        tipo: registro.tipo || "Informações não preenchidas",
        deficiencia: formatarDeficiencia(registro.deficiencia),
        nomecontato: registro.nomecontato || "Informações não preenchidas",
        cargo: registro.cargo || "Informações não preenchidas",
        obs: registro.obs || "Informações não preenchidas",
        obswp: registro.obswp || "Informações não preenchidas",
      };

      axios
        .get("https://museucatavento.org.br/envio/envioaprovacao.php", {
          params: dadosRegistro,
        })
        .then(() => {
          alert("Enviado para o usuário.");
        })
        .catch((error) => {
          console.error("Erro ao enviar e-mail:", error);
        });
    };

    const carregarRegistros = (param, diaselecionado, idadeSelecionada) => {
      isLoading.value = true;

      axios
        .post("https://museucatavento.org.br/sistema-agendamento/api2.php", {
          email: param,
          diaselecionado,
          idade: idadeSelecionada,
        })
        .then((response) => {
          if (response.data && response.data.length > 0) {
            registros.value = response.data.filter((item) => item != null);
          } else {
            registros.value = [];
            alert("Não foram encontrados registros para os filtros selecionados.");
          }
        })
        .catch((error) => {
          console.error(error);
          alert("Houve algum erro, por favor, tente novamente em instantes.");
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    const normalizarTexto = (valor) => String(valor ?? "").toLowerCase().trim();

    const registroContemIdade = (registro, idadeSelecionada) => {
      if (!idadeSelecionada) {
        return true;
      }

      const series = registro.series;
      const idadeNormalizada = normalizarTexto(idadeSelecionada);

      if (Array.isArray(series)) {
        return series.some((serie) => normalizarTexto(serie) === idadeNormalizada);
      }

      return normalizarTexto(series).includes(idadeNormalizada);
    };

    const registrosFiltrados = computed(() => {
      let filtrados = registros.value;

      if (filtrodia.value) {
        filtrados = filtrados.filter((registro) => registro.diamaster === filtrodia.value);
      }

      if (filtroIdade.value) {
        filtrados = filtrados.filter((registro) => registroContemIdade(registro, filtroIdade.value));
      }

      return [...filtrados].sort((a, b) => {
        const dataA = new Date(a.logsregistro || a.diamaster || 0).getTime();
        const dataB = new Date(b.logsregistro || b.diamaster || 0).getTime();
        return dataB - dataA;
      });
    });

    const limparFiltros = () => {
      filtrodia.value = "";
      filtroIdade.value = "";
      buscar();
    };

    onMounted(() => {
      carregarRegistros(emails);
      administrador.value = emails === "admin.agendamento@cataventomaster.com";
    });

    return {
      administrador,
      aprovar,
      abrirModalCancelamento,
      buscar,
      confirmarCancelamento,
      eadminsera,
      emails,
      extrairoteiro,
      filtrodia,
      filtroIdade,
      formatarData,
      formatarDeficiencia,
      isAgencia,
      isLoading,
      justificativaCancelamento,
      limparFiltros,
      opcoesIdade,
      registroSelecionado,
      registros,
      registrosFiltrados,
      registroTicket,
      statusClass,
      statusLabel,
      statusRegistro,
      valorCampo,
    };
  },
};
</script>

<style lang="scss" scoped>
.requests-page {
  background: #f5f7fa;
  min-height: 100vh;
  padding: clamp(1.25rem, 3vw, 2.5rem) 0 3rem;
  text-align: left;
}

.requests-shell {
  width: min(1180px, calc(100% - 2rem));
  margin: 0 auto;
}

.requests-header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.eyebrow {
  color: var(--catavento-orange);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.requests-header h1 {
  margin: 0;
}

.header-copy {
  color: var(--catavento-muted);
  max-width: 680px;
  margin: 0.6rem 0 0;
}

.user-card,
.filters-panel,
.record-card,
.empty-state {
  background: var(--catavento-surface);
  border: 1px solid var(--catavento-border);
  border-radius: var(--catavento-radius);
  box-shadow: 0 8px 22px rgba(20, 30, 45, 0.08);
}

.user-card {
  min-width: min(360px, 100%);
  padding: 1rem;
}

.user-card span {
  color: var(--catavento-muted);
  display: block;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.user-card strong {
  color: var(--catavento-text);
  display: block;
  margin-top: 0.25rem;
  overflow-wrap: anywhere;
}

.filters-panel {
  align-items: end;
  display: grid;
  grid-template-columns: minmax(180px, 0.8fr) minmax(220px, 1fr) auto;
  gap: 0.85rem;
  margin-bottom: 1rem;
  padding: 1rem;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 800;
}

.filter-actions {
  display: flex;
  gap: 0.65rem;
}

.results-toolbar {
  align-items: center;
  color: var(--catavento-muted);
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0;
}

.results-toolbar strong {
  color: var(--catavento-text);
  font-size: 1.2rem;
}

.results-toolbar p {
  margin: 0;
}

.records-list {
  display: grid;
  gap: 1rem;
}

.record-card {
  border-left: 5px solid transparent;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 1rem;
  padding: 1rem;
}

.record-card.agency {
  background: linear-gradient(90deg, rgba(22, 90, 150, 0.06), #fff 36%);
}

.record-title-line {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.record-title-line h2 {
  color: var(--catavento-text);
  font-size: 1.15rem;
  font-weight: 800;
  margin: 0;
}

.record-meta {
  color: var(--catavento-muted);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.86rem;
  margin-top: 0.45rem;
}

.status-badge {
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0.25rem 0.65rem;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.status-confirmado {
  background: #e6f4ed;
  color: #176847;
}

.status-pendente {
  background: #fff4db;
  color: #8a5200;
}

.status-cancelado {
  background: #fde8e8;
  color: #9b1c1c;
}

.status-reservado {
  background: #eef1f5;
  color: #475467;
}

.status-cancelamento {
  background: #fff8d7;
  color: #7a4b00;
}

.route-highlight {
  background: #eff6ff;
  border: 1px solid #c9ddf3;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.85rem;
  padding: 0.75rem;
}

.route-highlight span,
dt {
  color: var(--catavento-muted);
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.route-highlight strong {
  color: var(--catavento-blue);
  line-height: 1.35;
}

.info-grid,
.details-grid,
.agency-block dl {
  display: grid;
  gap: 0.75rem;
  margin: 0;
}

.info-grid,
.agency-block dl {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 0.95rem;
}

.details-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

dt,
dd {
  margin: 0;
}

dd {
  color: var(--catavento-text);
  overflow-wrap: anywhere;
}

.agency-block {
  border-top: 1px solid var(--catavento-border);
  margin-top: 1rem;
  padding-top: 1rem;
}

.agency-block p {
  color: var(--catavento-blue);
  font-weight: 800;
  margin: 0 0 0.75rem;
}

.record-side {
  border-left: 1px solid var(--catavento-border);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-left: 1rem;
}

.ticket-note {
  background: #f8fafc;
  border: 1px solid var(--catavento-border);
  border-radius: 6px;
  padding: 0.85rem;
}

.ticket-note span {
  color: var(--catavento-muted);
  display: block;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.ticket-note strong {
  color: var(--catavento-text);
  display: block;
  font-size: 1.25rem;
  margin-top: 0.2rem;
}

.details-accordion {
  grid-column: 1 / -1;
}

.details-accordion .accordion-item {
  border: 1px solid var(--catavento-border);
  border-radius: 6px;
  overflow: hidden;
}

.details-accordion .accordion-button {
  background: #f8fafc;
  color: var(--catavento-text);
  font-weight: 800;
  min-height: 42px;
}

.details-accordion .accordion-body {
  background: #fff;
}

.details-wide {
  grid-column: 1 / -1;
}

.empty-state {
  padding: 2rem;
  text-align: center;
}

.empty-state h2 {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: var(--catavento-muted);
  margin: 0;
}

.loading-overlay {
  align-items: center;
  background: rgba(17, 24, 39, 0.55);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1200;
}

.loading-box {
  align-items: center;
  background: #fff;
  border-radius: var(--catavento-radius);
  box-shadow: var(--catavento-shadow);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  text-align: center;
  width: min(360px, 100%);
}

.loading-box p {
  margin: 0;
}

#cancelare {
  max-width: 100%;
}

@media (max-width: 992px) {
  .requests-header,
  .results-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .filters-panel,
  .record-card {
    grid-template-columns: 1fr;
  }

  .record-side {
    border-left: none;
    border-top: 1px solid var(--catavento-border);
    padding-left: 0;
    padding-top: 1rem;
  }
}

@media (max-width: 767px) {
  .requests-shell {
    width: min(100% - 1rem, 1180px);
  }

  .record-title-line,
  .filter-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .info-grid,
  .details-grid,
  .agency-block dl {
    grid-template-columns: 1fr;
  }
}
</style>
